'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', href: '/homepage' },
    { label: 'Services', href: '/services' },
    { label: 'Service Areas', href: '/service-areas' },
    { label: 'Why Us', href: '/why-us' },
    { label: 'Request Quote', href: '/request-quote', isHighlight: true },
  ];

  const moreMenuItems = [
    { label: 'FAQ', href: '/faq' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
  ];

  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-background border-b border-border ${className}`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 md:px-6 lg:px-8">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-2 flex-shrink-0">
            <div className="w-10 h-10 bg-desert-gradient rounded-lg flex items-center justify-center shadow-brand">
              <svg
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-7 h-7"
              >
                {/* Water drop shape */}
                <path
                  d="M20 6C20 6 12 14 12 21C12 25.4183 15.5817 29 20 29C24.4183 29 28 25.4183 28 21C28 14 20 6 20 6Z"
                  fill="white"
                  opacity="0.95"
                />
                {/* Inner highlight for depth */}
                <path
                  d="M17 18C17 18 15 20 15 22C15 23.6569 16.3431 25 18 25C18.5 25 19 24.8 19.4 24.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.4"
                />
              </svg>
            </div>
            <div className="hidden sm:block">
              <span className="font-headline font-bold text-lg text-primary">Phoenix Patio Pro</span>
              <p className="text-xs text-muted-foreground font-body">Desert Cleaning Experts</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-4 py-2 rounded-md font-body font-medium transition-brand ${
                  item.isHighlight
                    ? 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-cta-glow hover:shadow-cta-glow-hover'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Click-to-Call Phone Number */}
            <a
              href="tel:+16027773005"
              className="flex items-center space-x-2 px-4 py-2 rounded-md font-body font-semibold text-primary hover:bg-muted transition-brand border-2 border-primary/20 hover:border-primary/40"
            >
              <Icon name="PhoneIcon" size={18} className="text-primary" />
              <span>(602) 777-3005</span>
            </a>

            {/* More Menu */}
            <div className="relative">
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className="px-4 py-2 rounded-md font-body font-medium text-foreground hover:bg-muted hover:text-primary transition-brand flex items-center space-x-1"
              >
                <span>More</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isMoreMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsMoreMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-md shadow-brand-elevated z-50">
                    {moreMenuItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className="block px-4 py-2 text-sm font-body text-popover-foreground hover:bg-muted transition-brand first:rounded-t-md last:rounded-b-md"
                        onClick={() => setIsMoreMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-brand"
            aria-label="Toggle menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              className="text-foreground"
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background">
            <nav className="px-4 py-4 space-y-2">
              {/* Click-to-Call Phone Number - Mobile */}
              <a
                href="tel:+16027773005"
                className="flex items-center justify-center space-x-2 px-4 py-3 rounded-md font-body font-semibold text-primary bg-primary/10 hover:bg-primary/20 transition-brand border-2 border-primary/30"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Icon name="PhoneIcon" size={20} className="text-primary" />
                <span>(602) 777-3005</span>
                <span className="text-xs text-muted-foreground ml-2">Tap to Call</span>
              </a>

              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`block px-4 py-3 rounded-md font-body font-medium transition-brand ${
                    item.isHighlight
                      ? 'bg-accent text-accent-foreground shadow-cta-glow'
                      : 'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              <div className="pt-2 border-t border-border mt-2">
                {moreMenuItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="block px-4 py-3 rounded-md font-body font-medium text-foreground hover:bg-muted hover:text-primary transition-brand"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;